/* hk-plp.js — shared product-listing behaviour for the collection page and the
   search results page. Extracted verbatim from hk-collection-page.liquid on
   13/08/2026 so the two pages cannot drift apart. Both sections load this with
   `defer`, so the DOM is parsed before any of it runs.

   Four independent bits:
   1. Filter bar — dropdown open/close, sort submits the GET form.
   2. Mobile overlays — the Filters drawer and the Sort sheet.
   3. Type-to-narrow inside any filter panel with a long value list.
   4. Load more — appends the next page into the grid instead of navigating. */

/* Filter bar: dropdown open and close, sort submits the filter form on change.
   Filtering itself is native Shopify via GET params. */
(function () {
  'use strict';

  var bar = document.getElementById('collection-filterbar');
  if (!bar) return;

  /* The bar used to be sticky, with its top offset measured from the live
     header here. It scrolls with the page now (Sean, 07/08), so there is
     nothing to measure: setting `top` on the now position:relative bar would
     shove it down the page. */

  var filters = Array.prototype.slice.call(bar.querySelectorAll('.collection-filter'));

  function closeAll(except) {
    filters.forEach(function (f) {
      if (f !== except) {
        f.classList.remove('is-open');
        f.querySelector('.collection-filter-btn').setAttribute('aria-expanded', 'false');
      }
    });
  }

  filters.forEach(function (filter) {
    var btn = filter.querySelector('.collection-filter-btn');

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = filter.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) closeAll(filter);
    });

    filter.addEventListener('click', function (e) { e.stopPropagation(); });

    if (filter.getAttribute('data-filter') === 'sort') {
      filter.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.addEventListener('change', function () { bar.submit(); });
      });
    }
  });

  document.addEventListener('click', function () { closeAll(null); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });
})();

/* Mobile overlays. Below 720px the chip row is off-screen inside a drawer and
   the sort chip is a bottom sheet; these two buttons are the only way in. The
   markup is the same on both breakpoints, so all of this is class toggling. */
(function () {
  'use strict';

  var bar = document.getElementById('collection-filterbar');
  if (!bar) return;

  var drawer = bar.querySelector('[data-filter-groups]');
  var sheet = bar.querySelector('.collection-filter-sort');
  var scrim = bar.querySelector('[data-plp-scrim]');
  var openFilters = bar.querySelector('[data-open-filters]');
  var openSort = bar.querySelector('[data-open-sort]');
  if (!drawer || !sheet || !scrim) return;

  scrim.removeAttribute('hidden');

  function close() {
    drawer.classList.remove('is-open');
    sheet.classList.remove('is-open');
    scrim.classList.remove('is-open');
    document.body.classList.remove('plp-locked');
    if (openFilters) openFilters.setAttribute('aria-expanded', 'false');
    if (openSort) openSort.setAttribute('aria-expanded', 'false');
  }

  function open(el, trigger) {
    close();
    el.classList.add('is-open');
    scrim.classList.add('is-open');
    document.body.classList.add('plp-locked');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  if (openFilters) {
    openFilters.addEventListener('click', function (e) {
      e.stopPropagation();
      open(drawer, openFilters);
    });
  }

  if (openSort) {
    openSort.addEventListener('click', function (e) {
      e.stopPropagation();
      open(sheet, openSort);
    });
  }

  bar.querySelectorAll('[data-close-filters], [data-close-sort]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      close();
    });
  });

  scrim.addEventListener('click', close);

  /* The drawer is a click island: taps inside it must not reach the document
     handler that collapses every open accordion. */
  drawer.addEventListener('click', function (e) { e.stopPropagation(); });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });

  /* Coming back to a wide viewport with an overlay up would leave the body
     locked and the scrim painted over the grid. */
  window.addEventListener('resize', function () {
    if (window.innerWidth > 720 && document.body.classList.contains('plp-locked')) close();
  });
})();

/* Type-to-narrow. Brand runs past 40 values on the bigger shelves, so long
   panels carry a search field. It only hides rows: nothing is removed from the
   form, so a value checked before typing still submits. */
(function () {
  'use strict';

  var fields = document.querySelectorAll('[data-filter-search]');
  if (!fields.length) return;

  fields.forEach(function (field) {
    var panel = field.closest('.collection-filter-panel');
    if (!panel) return;

    var options = Array.prototype.slice.call(panel.querySelectorAll('.collection-option'));
    var none = panel.querySelector('.collection-filter-none');

    field.addEventListener('input', function () {
      var term = field.value.trim().toLowerCase();
      var shown = 0;

      options.forEach(function (option) {
        var label = option.getAttribute('data-value-label') || '';
        var checked = option.querySelector('input') && option.querySelector('input').checked;
        /* A ticked value stays visible whatever is typed, so it is never
           hidden mid-edit and quietly submitted anyway. */
        var match = term === '' || checked || label.indexOf(term) !== -1;
        option.hidden = !match;
        if (match) shown++;
      });

      if (none) none.hidden = shown > 0;
    });

    /* Enter in the field would submit the form and lose the panel. */
    field.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });
  });
})();

/* Load more: append the next page into the grid instead of navigating to it. */
(function () {
  'use strict';

  var grid = document.querySelector('[data-collection-grid]');
  var more = document.querySelector('[data-collection-more]');
  if (!grid || !more) return;

  var loading = false;

  /* Previous only exists for the no-JS path. */
  function dropPrev() {
    var prev = more.querySelector('[data-load-prev]');
    if (prev) prev.remove();
  }

  function bind() {
    dropPrev();
    var btn = more.querySelector('[data-load-more]');
    if (btn) btn.addEventListener('click', onClick);
  }

  function onClick(e) {
    if (loading) { e.preventDefault(); return; }
    var btn = e.currentTarget;
    var url = btn.getAttribute('href');
    if (!url) return;

    e.preventDefault();
    loading = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Loading';

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextGrid = doc.querySelector('[data-collection-grid]');
        var nextMore = doc.querySelector('[data-collection-more]');
        if (!nextGrid || !nextMore) throw new Error('markup');

        /* Remember where the new run starts so focus can land on it. */
        var firstNew = nextGrid.firstElementChild;
        while (nextGrid.firstElementChild) {
          grid.appendChild(nextGrid.firstElementChild);
        }

        /* Update the count in place rather than rebuilding the block, so the
           aria-live node survives and the new total is actually announced. */
        var count = more.querySelector('.meta-sm');
        var nextCount = nextMore.querySelector('.meta-sm');
        if (count && nextCount) count.textContent = nextCount.textContent;

        var nextBtn = nextMore.querySelector('[data-load-more]');
        if (nextBtn) {
          btn.setAttribute('href', nextBtn.getAttribute('href'));
          btn.textContent = 'Load more';
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        } else {
          btn.remove();
        }
        loading = false;

        if (firstNew && firstNew.tagName === 'A') {
          firstNew.setAttribute('tabindex', '-1');
          firstNew.focus({ preventScroll: true });
        }
      })
      .catch(function () {
        /* Never strand the shopper on a dead button: fall back to the real
           paginate URL, which is what the href has been all along. */
        window.location.href = url;
      });
  }

  bind();
})();
